import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import axios from 'axios';
import api from '../../utils/api';
import UnifiedSupplierProfile from './UnifiedSupplierProfile';

function ViewSupplierProfile() {
  const { supplierId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isAdminView = location.pathname.startsWith('/admin/');

  useEffect(() => {
    fetchSupplierProfile();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [supplierId, isAdminView]);

  const fetchSupplierProfile = async () => {
    try {
      setLoading(true);
      setError('');

      let response;
      if (isAdminView) {
        // Admin/support must send auth so unverified suppliers remain viewable
        response = await api.get(`/api/supplier/${supplierId}`);
      } else {
        // Public couple-facing view: no auth headers
        const publicApi = axios.create({
          baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001',
          headers: {
            'Content-Type': 'application/json'
          }
        });
        response = await publicApi.get(`/api/supplier/${supplierId}`);
      }

      setSupplier(response.data);
    } catch (err) {
      if (err.response) {
        if (err.response.status === 403 || err.response.status === 401) {
          setError('Access denied. You do not have permission to view this supplier profile.');
        } else if (err.response.status === 404) {
          setError(isAdminView
            ? 'Supplier not found. This business may have been deleted.'
            : 'Supplier not found. The profile you are looking for does not exist.');
        } else {
          setError(`Failed to load supplier profile: ${err.response.data.message || 'Unknown error'}`);
        }
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnquireClick = () => {
    navigate(`/book-supplier/${supplierId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!supplier) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Supplier not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
      </Box>
      <UnifiedSupplierProfile
        supplier={supplier}
        loading={loading}
        error={error}
        isPublic={!isAdminView}
        onEnquireClick={isAdminView ? undefined : handleEnquireClick}
      />
    </Box>
  );
}

export default ViewSupplierProfile;
